// src/api/utils/tokenRefreshContext.ts
/**
 * Simple in-memory context that holds the current authentication state and
 * serialises concurrent token-refresh attempts. It is deliberately lightweight
 * and does not depend on any external libraries so it can be used from the
 * Axios interceptor as also React hooks.
 */

export interface AuthState {
  accessToken: string;
  refreshToken: string;
  expiresAt: Date;
  user: any; // UserInfoDTO – keep loosely typed to avoid circular imports
  isAuthenticated: boolean;
}

// Internal mutable store – not exported directly to enforce encapsulation
let authState: AuthState | null = null;

/**
 * Retrieve the current auth state. Returns null if the user is not logged in
 * **or** if the access token has expired. This is the default behaviour used by
 * most of the application to decide whether a request should be considered
 * authenticated.
 */
export function getAuthState(): AuthState | null {
  // If the token is expired, treat as unauthenticated (caller can trigger refresh)
  if (authState && authState.expiresAt.getTime() <= Date.now()) {
    return null;
  }
  return authState;
}

/**
 * Retrieve the raw auth state **including** an expired token. This is required by
 * the Axios request interceptor which must still send the (expired) token so that
 * the backend can respond with a 401 and trigger the refresh flow.
 */
export function getRawAuthState(): AuthState | null {
  return authState;
}

/**
 * Replace the stored auth state. Passing `null` clears the session (logout).
 */
export function setAuthState(state: AuthState | null): void {
  authState = state;
}

/**
 * Queue used to serialize token refresh calls. When a refresh is in progress,
 * additional requests are pushed onto this queue and resolved once the refresh
 * completes (or fails).
 */
let isRefreshing = false;
const refreshQueue: Array<() => void> = [];

/**
 * Execute a function that requires a fresh token. If a refresh is already in
 * progress, the function will be queued and invoked after the refresh finishes.
 * The returned promise resolves when the function has been executed.
 */
export function enqueueRefresh<T>(fn: () => Promise<T>): Promise<T> {
  if (!isRefreshing) {
    // No refresh in progress – run immediately
    return fn();
  }
  // Refresh in progress – return a promise that resolves after the queue is flushed
  return new Promise<T>((resolve, reject) => {
    refreshQueue.push(() => {
      fn().then(resolve).catch(reject);
    });
  });
}

/**
 * Called by the Axios interceptor before starting a token refresh. It marks the
 * refresh as active and returns a function that should be called once the refresh
 * completes (success or failure) to release queued requests.
 */
export function startRefresh(): () => void {
  isRefreshing = true;
  return () => {
    isRefreshing = false;
    // Resolve all queued callbacks
    const queued = [...refreshQueue];
    refreshQueue.length = 0;
    queued.forEach((cb) => cb());
  };
}

/**
 * Helper to determine if a refresh is currently happening. Primarily for testing.
 */
export function isRefreshInProgress(): boolean {
  return isRefreshing;
}
